import { Body } from 'matter-js';
import { isMobile } from 'react-device-detect';

import { Dim, Pos } from './models';
import { INPUT_MODES } from '../../../../constants/defaults';
import { getPx } from '../../../../helpers';

export { getPx }; // For convenience

export const dimsOf = (body: Body): Dim => ({
  w: body.bounds.max.x - body.bounds.min.x,
  h: body.bounds.max.y - body.bounds.min.y,
});

export const posOf = (body: Body): Pos => ({
  x: body.position.x,
  y: body.position.y,
});

export const getNewY = (
  endY: number,
  timing: number,
  elapsed: number,
  velocity: number,
  scale = -1
): number => {
  const startY = endY + scale * timing * velocity;
  const progress = elapsed * velocity;

  return startY - scale * progress;
};

const jogPanelW = (wW) => Math.min(wW / (isMobile ? 4 : 6), 75);
const jogPanelH = (wH) => Math.min(wH / (isMobile ? 9 : 10), 75);
const compJogDims = (windowDims) =>
  jogPanelW(windowDims.w) < jogPanelH(windowDims.h);
const minJogDim = (windowDims) =>
  Math.min(jogPanelW(windowDims.w), jogPanelH(windowDims.h));
const gymPanelRatio = 1.4;
const gymPanelW = (wW) => Math.min(wW / (isMobile ? 5 : 6), 105);
const gymPanelH = (wH) => Math.min(wH / (isMobile ? 13 : 14.5), 75);
const compGymDims = (windowDims) =>
  gymPanelW(windowDims.w) < gymPanelH(windowDims.h);
const minGymDim = (windowDims) =>
  Math.min(gymPanelW(windowDims.w), gymPanelH(windowDims.h));
const panelB = (dim, d) => Math.min(dim / d, 3);

export const getPanelDims = (windowDims: Dim): Record<string, Dim> => ({
  key: {
    h: minGymDim(windowDims),
    b: panelB(minGymDim(windowDims), compGymDims(windowDims) ? 35 : 25),
    m: panelB(minGymDim(windowDims), compGymDims(windowDims) ? 35 : 25),
  } as Dim,
  jog: {
    w: minJogDim(windowDims),
    h: minJogDim(windowDims),
    b: panelB(minJogDim(windowDims), compJogDims(windowDims) ? 35 : 25),
    m: panelB(minJogDim(windowDims), compJogDims(windowDims) ? 35 : 25),
  },
  gym: {
    w: compGymDims(windowDims)
      ? gymPanelW(windowDims.w)
      : gymPanelH(windowDims.h) * gymPanelRatio,
    h: compGymDims(windowDims)
      ? gymPanelW(windowDims.w) / gymPanelRatio
      : gymPanelH(windowDims.h),
    b: panelB(minGymDim(windowDims), compGymDims(windowDims) ? 35 : 25),
    m: panelB(minGymDim(windowDims), compGymDims(windowDims) ? 35 : 25),
  },
});

export const getNoteDims = (windowDims: Dim): Dim => ({
  w: minGymDim(windowDims),
  h: minGymDim(windowDims),
  b: panelB(minGymDim(windowDims), compGymDims(windowDims) ? 35 : 25),
});

export const getReceptorDims = (windowDims: Dim): Dim => {
  const panelDims = getPanelDims(windowDims);
  const spacedHeight = getNoteDims(windowDims).h + 4 * (panelDims.jog.b ?? 0);

  return {
    w: 3 * spacedHeight,
    h: spacedHeight,
    b: 2 * (panelDims.jog.b ?? 0),
  };
};

export const getReceptorY = (
  windowDims: Dim,
  receptorDims: Dim,
  inputMode?: string
): number => {
  const safeInputMode = !inputMode ? INPUT_MODES.key : inputMode;
  const safeDims = getPanelDims(windowDims)[safeInputMode] as Dim;
  const panelS = 2 * (safeDims.b ?? 0) + 2 * (safeDims.m ?? 0);
  const spacedHeight = safeDims.h + panelS;
  const multipliers = { key: 2, jog: 3, gym: 6 };
  const padHeight = multipliers[safeInputMode] * spacedHeight;

  return windowDims.h - padHeight - receptorDims.h / 2;
};
